import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { trainingStateAtom, validatingStateAtom } from "../recoil/upgrade";
import { toast } from "react-toastify";
import { swBridge } from "../utils/sw-register";
import { BASE_URL } from "../services/client";

interface aiModuleDescType {
  name: string;
  epoch?: number;
  infer: boolean;
  is_complete: boolean;
  iteration?: number;
  preview: boolean;
  total: number;
  train: boolean;
  trained_count: number;
}

interface aiModuleType {
  uuid: string;
  serverAddress: string;
  desc: aiModuleDescType;
  options: any;
}

type ProgressPayload = {
  count: number;
  totalCount: number;
  results?: any[]; // 추가
};

interface streamProps {
  enabled: boolean;
  trainUuid: string | null;
  projectData: any;
  trainedImageCount: number;
  sendCount: number;
  myAIModule: any;
  site: string;
  mode: "train" | "valid" | "preview";
  onProgress: (result: any) => void;
  onComplete: () => void;
  onError: (err: any) => void;
  id: string | undefined;
  label?: string;
  epoch?: number;
  setMyAIModule: (module: aiModuleType | null) => void;
}

const SW_AVAILABLE = "serviceWorker" in navigator;
const WATCHDOG_INTERVAL = 10000;

export function useTrainingStream({
  enabled,
  trainUuid,
  projectData,
  myAIModule,
  mode,
  onProgress,
  onComplete,
  onError,
  id,
  setMyAIModule,
}: streamProps) {
  const [, setTrainingState] = useRecoilState(trainingStateAtom);
  const [, setValidatingState] = useRecoilState(validatingStateAtom);
  const isFinishedRef = useRef(false);
  const pollingStoppedRef = useRef(false); // 의도적으로 멈춘 경우 watchdog이 되살리지 않도록
  const lastMsgAtRef = useRef(0);
  // 리스너는 최신 값을 ref로 읽는다. myAIModule을 effect deps에 넣으면 handleDone의 setMyAIModule이
  // effect를 재실행 → isFinishedRef 리셋 + startPolling 재호출 → done 재수신의 무한 루프가 된다.
  const latestRef = useRef({ myAIModule, onProgress, onComplete, onError });
  latestRef.current = { myAIModule, onProgress, onComplete, onError };

  const handleDone = (uuid: string) => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;
    const { myAIModule, onComplete } = latestRef.current;

    if (SW_AVAILABLE) {
      if (mode !== "train") {
        pollingStoppedRef.current = true;
        swBridge.stopPolling(uuid);
        swBridge.clearCache(uuid);
      }
    }

    setMyAIModule({
      ...myAIModule,
      desc: { ...myAIModule.desc, infer: false, train: false, preview: true },
    });

    setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
    onComplete();
  };

  const handleError = (uuid: string, error: any) => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;
    const { myAIModule, onError } = latestRef.current;

    if (SW_AVAILABLE) {
      pollingStoppedRef.current = true;
      swBridge.stopPolling(uuid);
      swBridge.clearCache(uuid);
    }

    toast.error("학습 중 오류가 발생했습니다");
    setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
    setMyAIModule({
      ...myAIModule,
      desc: { ...myAIModule.desc, infer: false, train: false, preview: true },
    });
    onError(error);
  };

  useEffect(() => {
    if (!trainUuid || !myAIModule || !projectData) return;
    if (!SW_AVAILABLE) {
      console.warn("[useTrainingStream] SW 미지원 환경 (HTTP IP 접근). SW 기능 비활성화.");
      return;
    }

    const pollUrl = `${BASE_URL}/ai/progress/${trainUuid}`;
    let watchdog: ReturnType<typeof setInterval> | undefined;
    let kick: (() => void) | undefined;
    let onVisible: (() => void) | undefined;

    // enabled가 false여도 새로고침 이전부터 SW가 이미 해당 uuid를 polling 중일 수 있으므로
    // 메시지 리스너는 항상 붙여두고, 새로 polling을 시작시키는 것만 enabled로 제한한다.
    if (enabled) {
      // 새로 시작할 때만 리셋. handleDone → enabled=false 재실행에서 리셋하면
      // 계속 오는 valid_starting을 매 poll마다 다시 처리한다(setMyAIModule/onComplete 반복).
      isFinishedRef.current = false;
      pollingStoppedRef.current = false;
      lastMsgAtRef.current = Date.now();
      swBridge.startPolling(trainUuid, pollUrl);

      // 브라우저는 유휴 SW를 수십 초 만에 종료하고, 그러면 폴링 루프가 조용히 사라진다
      // (요청만 끊기고 에러는 안 난다 = 진행도가 멈춘 채로 보임). 주기적으로 kick해서 되살린다.
      // 폴링이 살아있으면 SW쪽 중복 가드에 걸려 no-op이라 평소엔 아무 일도 안 한다.
      kick = () => {
        if (pollingStoppedRef.current) return;
        const idle = Date.now() - lastMsgAtRef.current;
        if (idle > WATCHDOG_INTERVAL) console.warn(`[SW] ${idle}ms 동안 진행도 수신 없음: ${trainUuid}`);
        swBridge.startPolling(trainUuid, pollUrl, true);
      };
      watchdog = setInterval(kick, WATCHDOG_INTERVAL);
      // 백그라운드 탭은 setInterval이 1분까지 throttle되므로 돌아오는 즉시 한 번 더
      onVisible = () => document.visibilityState === "visible" && kick!();
      document.addEventListener("visibilitychange", onVisible);
    }

    const handleSWMessage = (event: MessageEvent) => {
      const { type, uuid, data } = event.data ?? {};
      if (type !== "PROGRESS_UPDATE" || uuid !== trainUuid) return;
      lastMsgAtRef.current = Date.now();
      if (data.state === "not_found_limit") {
        handleError(uuid, new Error("progress not found"));
        return;
      }

      if (isFinishedRef.current) return;
      if (!data) return;
      const { onProgress } = latestRef.current;

      if (data.state === "running") {
        // /progress가 실제로 running을 응답하면 해당 projectId는 학습(검사) 중인 것이 확실하므로,
        // 다른 경로(inspStatus 등)에서 state가 잘못 false로 덮인 경우를 여기서 바로 교정한다.
        if (mode === "valid" && data.type === "valid") {
          setValidatingState((prev: any) => ({ ...prev, [Number(id)]: true }));
          const extracted = (data.items ?? []).map((item: any) => ({
            id: item.path,
            AILabel: item.label,
            score: item.score,
            path: item.path,
          }));
          onProgress({ extracted, count: data.count, totalCount: data.totalCount });
        }
        if (mode === "train" && data.type === "train") {
          if (isFinishedRef.current) return;
          setTrainingState((prev: any) => ({ ...prev, [Number(id)]: true }));
          onProgress({ count: data.count, totalCount: data.totalCount });
        }
        return;
      }
      if (data.state === "stalled") {
        //stopTrain진행 및 관련 history도 업데이트
        pollingStoppedRef.current = true;
        swBridge.stopPolling(uuid);
        swBridge.clearCache(uuid);
        setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
        setValidatingState((prev: any) => ({ ...prev, [Number(id)]: true }));
      }

      // ✅ 기존 done 블록 교체
      if (data.state === "done") {
        if (data.type === "train") {
          onProgress({ count: data.totalCount, totalCount: data.totalCount });
          return;
        }
        if (data.type === "valid") {
          if (isFinishedRef.current) return;
          handleDone(uuid);
          return;
        }
      }

      if (data.state === "valid_starting") {
        if (mode === "train") {
          // 폴링은 SW가 valid 단계까지 이어가므로 여기서 멈추지 않는다
          setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
          setValidatingState((prev: any) => ({ ...prev, [Number(id)]: true }));
          handleDone(uuid);
        }
        if (mode === "valid") {
          // 검사 시작 전엔 progress가 학습 완료(100%) 값으로 남아있으므로 0부터 다시 표시
          latestRef.current.onProgress({ count: data.count, totalCount: data.totalCount });
        }
        return;
      }

      if (data.state === "error") {
        handleError(uuid, data.error);
        return;
      }
    };
    navigator.serviceWorker.addEventListener("message", handleSWMessage);

    return () => {
      if (watchdog) clearInterval(watchdog);
      if (onVisible) document.removeEventListener("visibilitychange", onVisible);
      navigator.serviceWorker.removeEventListener("message", handleSWMessage);
    };
  }, [enabled, trainUuid, !!projectData, !!myAIModule]);
}
